import { computed, ref } from 'vue'
import { type RouteLocationRaw, useRouter } from 'vue-router'
import { useFeatures } from '@/composables/useFeatures'
import { t } from '@/i18n/t'
import type { Customer, Service } from '@/types/api'
import { api } from '@/utils/apiClient'
import { errorMessage } from '@/utils/apiError'
import { fuzzyMatchAny, fuzzyNormalize, fuzzySuggestions } from '@/utils/fuzzySearch'
import { type HitGroup, rankAndGroup, type ScorableHit } from '@/utils/searchScoring'

// useGlobalSearch — the data half of the "smart søk" modal (GlobalSearchModal).
//
// A single query fans out across ten object types and the ranked, grouped
// result is context-aware: records the operator can already see on the page
// they opened search from float to the top (the +6 on-view boost lives in
// searchScoring). Empty-query state surfaces recent searches + "på denne
// siden" quick-jumps derived from the current route.
//
// Sources, by cost:
//   - Backend, per-query (debounced): tags (meetings/customers/documents),
//     action-points, notes (authored-only, decrypt-per-search), document
//     content (Tika-indexed, gated), messages (decrypt-per-search, gated),
//     agenda asks (plain, gated). Each already filters server-side; we
//     re-score client-side for one unified ranking.
//   - Client-side, over data loaded once on open: services, team, customers,
//     tags. These are bounded metadata (docs/search.md: client-side is correct
//     here). Customers match on name + email — the same fields the Kunder list
//     filters on; they route to the customer detail page. Backend tag hits win
//     over the client name hit for the same customer (dedup by id), so a
//     customer never doubles up.
//
// State is module-scoped so the modal (owns the input + runSearch) and the
// dashboard shell (owns navigateToHit + reset-on-close) share one result set.

// ── Public types ────────────────────────────────────────────────────
export type GlobalSearchTarget = {
  kind:
    | 'meeting'
    | 'customer'
    | 'document'
    | 'action_point'
    | 'note'
    | 'message'
    | 'service'
    | 'member'
    | 'tag'
    // 'route' backs the no-results escape-hatch actions (see alle kunder /
    // møter): the shell just pushes `to`.
    | 'route'
  id: string
  label: string
  // Only set for action_point / note / message hits — they drive
  // meeting/customer/conversation routing.
  booking_id?: string
  customer_id?: string
  // Only set for 'route' hits — the destination the shell navigates to.
  to?: RouteLocationRaw
}

// Display + preview metadata carried on each hit. The results row reads
// initials / isPrivate / tags; the Layout-B preview pane reads the rest.
export interface HitMeta {
  target: GlobalSearchTarget
  initials?: string
  isPrivate?: boolean
  tags: string[]
  when?: string
  svc?: string
  status?: string
  due?: string
  who?: string
  role?: string
  usage?: string
  body?: string
  // A body-content match excerpt (notes today): the results row shows it as a
  // highlighted subline so the operator sees WHERE the query matched.
  snippet?: string
}

export type SearchHit = ScorableHit<HitMeta>

// A "på denne siden" quick-jump card. `num`/`short` render the big-serif-
// number card; tapping either runs a search (`query`) or jumps to a route
// (`to`) — exactly one is set.
export interface QuickCard {
  num: string
  short: string
  clay?: boolean
  query?: string
  to?: RouteLocationRaw
}

// The context the shell feeds in on open: which object ids are on the page
// (for the boost) and the page's quick-jump cards.
export interface SearchContext {
  onViewIds: string[]
  quickCards: QuickCard[]
}

// ── Backend payloads ────────────────────────────────────────────────
interface TagHit {
  object_id: string
  label: string
  tags: string[]
}
interface TagResponse {
  meetings: TagHit[]
  customers: TagHit[]
  documents: TagHit[]
}
interface ActionPointHit {
  id: string
  label: string
  tag?: string
  booking_id?: string
  customer_id?: string
  // due_date (RFC3339) + overdue ride on every action-point hit from
  // followups/search.go, but were dropped on the client (#2386) — so the
  // BOOST_FORFALT ranking bump, the clay overdue styling, and the due-date
  // preview line were all dead wiring for real search results.
  due_date?: string
  overdue?: boolean
}
interface ActionPointResponse {
  action_points: ActionPointHit[]
}
interface NoteHit {
  id: string
  booking_id?: string
  title: string
  updated_at: string
  customer_name?: string
  customer_email?: string
  service_name?: string
  // Body-content match excerpt (empty when the query matched only metadata).
  snippet?: string
}
interface NoteResponse {
  items: NoteHit[]
}
// Document-content hits (DOK-SEARCH): the operator-indexed file's extracted
// text, with an excerpt cropped around the match. Only opt-in-indexable files
// reach this index (health docs stay out), gated on the documents.search flag.
interface DocContentHit {
  share_id: string
  document_id: string
  name: string
  category?: string
  direction: string
  excerpt: string
}
interface DocContentResponse {
  results: DocContentHit[]
}
// Message-content hits (MSG-SEARCH): a samtale message whose body matched,
// decrypted per-search server-side. The excerpt is carried as the snippet so
// the row highlights where in the conversation the query hit. Gated on the
// messaging.search flag.
interface MessageHit {
  message_id: string
  thread_id: string
  customer_id?: string
  customer_email: string
  customer_name: string
  sender_kind: string
  created_at: string
  snippet: string
}
interface MessageResponse {
  items: MessageHit[]
}
// Agenda-ask hits (AGENDA-SEARCH): a meeting agenda point ("Hva må vi få svar
// på?", kind='ask') whose text matched. Every ask hangs off a booking, so the
// hit deep-links into that meeting's prep. Gated on meetings.agenda-search.
interface AskHit {
  id: string
  label: string
  booking_id?: string
  customer_id?: string
  customer_name?: string
  service_name?: string
}
interface AskResponse {
  asks: AskHit[]
}
interface TeamResponse {
  members: Array<{ id: string; name: string; email: string }>
}

const DEBOUNCE_MS = 200
const RECENT_KEY = 'stemne.search.recent'
const RECENT_MAX = 6

// ── Module state ────────────────────────────────────────────────────
const query = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Backend-sourced hits (meetings/customers/documents/action-points/notes),
// set by the debounced fetch. Client hits (services/team/customers/tags) are
// computed reactively from the static caches, so they don't live here.
const backendHits = ref<SearchHit[]>([])

// Loaded once per session on first open. Empty until then.
const servicesCache = ref<Service[]>([])
const membersCache = ref<Array<{ id: string; name: string; email: string }>>([])
const customersCache = ref<Customer[]>([])
const tagCatalog = ref<string[]>([])
let staticLoaded = false

const context = ref<SearchContext>({ onViewIds: [], quickCards: [] })
const recent = ref<string[]>(loadRecent())

// resetSearchContext clears the page context. Module-level (no component
// scope) so a router.beforeEach guard can call it on every navigation —
// that's what stops one route's "på denne siden" cards + on-view boost from
// leaking onto the next page. Each view then re-adds its own context on mount.
export function resetSearchContext() {
  context.value = { onViewIds: [], quickCards: [] }
}

// ── Recent searches (localStorage) ──────────────────────────────────
function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((t) => typeof t === 'string').slice(0, RECENT_MAX)
      : []
  } catch {
    return []
  }
}
function persistRecent() {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value))
  } catch {
    // Best-effort; a full/blocked localStorage just means no history.
  }
}

// ── Hit builders ────────────────────────────────────────────────────
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function meetingHit(h: TagHit): SearchHit {
  return {
    id: h.object_id,
    type: 'mote',
    title: h.label,
    keywords: h.tags.join(' '),
    ctx: h.tags.map((t) => `#${t}`).join(' · '),
    payload: { target: { kind: 'meeting', id: h.object_id, label: h.label }, tags: h.tags },
  }
}
function customerHit(h: TagHit): SearchHit {
  return {
    id: h.object_id,
    type: 'kunde',
    title: h.label,
    keywords: h.tags.join(' '),
    ctx: h.tags.map((t) => `#${t}`).join(' · '),
    payload: {
      target: { kind: 'customer', id: h.object_id, label: h.label },
      initials: initialsOf(h.label),
      tags: h.tags,
    },
  }
}
function documentHit(h: TagHit): SearchHit {
  return {
    id: h.object_id,
    type: 'dokument',
    title: h.label,
    keywords: h.tags.join(' '),
    ctx: h.tags.map((t) => `#${t}`).join(' · '),
    payload: { target: { kind: 'document', id: h.object_id, label: h.label }, tags: h.tags },
  }
}
// A document matched on its extracted CONTENT — carries the excerpt as the
// snippet so the row highlights where in the file the query hit.
function docContentHit(d: DocContentHit): SearchHit {
  return {
    id: `doc-content-${d.share_id}`,
    type: 'dokument',
    title: d.name,
    keywords: d.name,
    ctx: [d.category, d.direction === 'in' ? 'mottatt' : 'delt'].filter(Boolean).join(' · '),
    payload: {
      target: { kind: 'document', id: d.document_id, label: d.name },
      tags: [],
      snippet: d.excerpt,
    },
  }
}
function actionPointHit(a: ActionPointHit): SearchHit {
  return {
    id: a.id,
    type: 'oppfolging',
    title: a.label,
    keywords: a.tag ?? '',
    ctx: a.tag ? `#${a.tag}` : '',
    // forfalt drives the +1.2 BOOST_FORFALT ranking bump (searchScoring) and
    // the sr__ctx--clay overdue styling (SearchResults); due feeds the
    // preview's due-date line (#2386).
    forfalt: a.overdue,
    payload: {
      target: {
        kind: 'action_point',
        id: a.id,
        label: a.label,
        booking_id: a.booking_id,
        customer_id: a.customer_id,
      },
      tags: a.tag ? [a.tag] : [],
      due: a.due_date,
    },
  }
}
function noteHit(n: NoteHit): SearchHit {
  const ctx = [n.service_name, n.customer_name, formatNoteDate(n.updated_at)]
    .filter(Boolean)
    .join(' · ')
  return {
    id: n.id,
    type: 'notat',
    title: n.title || 'Notat',
    keywords: [n.customer_name, n.customer_email, n.service_name].filter(Boolean).join(' '),
    ctx,
    payload: {
      target: { kind: 'note', id: n.id, label: n.title || 'Notat', booking_id: n.booking_id },
      isPrivate: true,
      tags: [],
      who: n.customer_name,
      svc: n.service_name,
      snippet: n.snippet,
    },
  }
}
function formatNoteDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })
}
// A meeting agenda point matched — the ask text is the title, context is the
// customer + service, and it routes into the meeting's prep (reusing the
// 'meeting' target kind, keyed on the ask's booking).
function askHit(a: AskHit): SearchHit {
  const ctx = [a.customer_name, a.service_name].filter(Boolean).join(' · ')
  return {
    id: `ask-${a.id}`,
    type: 'agenda',
    title: a.label,
    keywords: [a.customer_name, a.service_name].filter(Boolean).join(' '),
    ctx,
    payload: {
      target: {
        kind: 'meeting',
        id: a.booking_id ?? '',
        label: a.label,
        customer_id: a.customer_id,
      },
      tags: [],
    },
  }
}
// A message matched on its decrypted body — the row shows who + when and
// highlights the match via the snippet, and routes into the conversation.
function messageHit(m: MessageHit): SearchHit {
  const who = m.sender_kind === 'admin' ? t('globalSearch.messageFromYou') : m.customer_name
  const ctx = [who, formatNoteDate(m.created_at)].filter(Boolean).join(' · ')
  return {
    id: `msg-${m.message_id}`,
    type: 'melding',
    title: m.customer_name || m.customer_email,
    keywords: [m.customer_name, m.customer_email].filter(Boolean).join(' '),
    ctx,
    payload: {
      target: {
        kind: 'message',
        id: m.thread_id,
        label: m.customer_name || m.customer_email,
        customer_id: m.customer_id,
      },
      initials: initialsOf(m.customer_name || m.customer_email),
      tags: [],
      snippet: m.snippet,
    },
  }
}

function serviceHit(s: Service): SearchHit {
  const price = s.price_kr == null ? '' : s.price_kr === 0 ? 'gratis' : `${s.price_kr} kr`
  const mode = s.mode === 'office' ? 'på klinikk' : s.mode === 'both' ? 'video / klinikk' : 'video'
  const ctx = [`${s.duration_mins} min`, mode, price].filter(Boolean).join(' · ')
  return {
    id: s.id,
    type: 'tjeneste',
    title: s.name,
    keywords: [s.description, mode].filter(Boolean).join(' '),
    ctx,
    payload: {
      target: { kind: 'service', id: s.id, label: s.name },
      tags: [],
      status: s.active ? 'aktiv' : 'skjult',
    },
  }
}
function memberHit(m: { id: string; name: string; email: string }): SearchHit {
  return {
    id: m.id,
    type: 'radgiver',
    title: m.name,
    keywords: m.email,
    ctx: m.email,
    payload: {
      target: { kind: 'member', id: m.id, label: m.name },
      initials: initialsOf(m.name),
      tags: [],
      role: 'rådgiver',
    },
  }
}
// A customer matched by name or email (the Kunder-list fields), sourced from
// the client-side catalog rather than a tag. Routes to the customer detail
// page via the same 'customer' target kind as the tag-derived customerHit;
// context line shows the email so a name query disambiguates namesakes.
function customerNameHit(c: Customer): SearchHit {
  return {
    id: c.id,
    type: 'kunde',
    title: c.name,
    keywords: c.email,
    ctx: c.email,
    payload: {
      target: { kind: 'customer', id: c.id, label: c.name },
      initials: initialsOf(c.name),
      tags: c.tags ?? [],
    },
  }
}
function tagHit(tag: string, usageCount: number): SearchHit {
  const usage = usageCount > 0 ? `brukt på ${usageCount} treff` : 'tagg'
  return {
    id: `tag:${tag}`,
    type: 'tagg',
    title: `#${tag}`,
    keywords: tag,
    ctx: usage,
    payload: { target: { kind: 'tag', id: tag, label: `#${tag}` }, tags: [], usage },
  }
}

// ── Client-side hits (services / team / tags) ───────────────────────
// Recomputed reactively from the static caches on every query change —
// no network. Tags are derived from the fetched catalog + tags present on
// the current backend hits, with a usage count over the live result set.
const clientHits = computed<SearchHit[]>(() => {
  const raw = query.value.trim()
  if (!raw) return []
  const nq = fuzzyNormalize(raw)
  const out: SearchHit[] = []

  for (const s of servicesCache.value) {
    if (fuzzyMatchAny(nq, [s.name, s.description, s.mode])) out.push(serviceHit(s))
  }
  for (const m of membersCache.value) {
    if (fuzzyMatchAny(nq, [m.name, m.email])) out.push(memberHit(m))
  }

  // Customers by name/email. A customer the backend already surfaced as a tag
  // hit is skipped here so it never doubles up — the server-vouched tag hit
  // (which carries the #tag context) wins for that id.
  const backendCustomerIds = new Set(
    backendHits.value.filter((h) => h.type === 'kunde').map((h) => h.id),
  )
  for (const c of customersCache.value) {
    if (backendCustomerIds.has(c.id)) continue
    if (fuzzyMatchAny(nq, [c.name, c.email])) out.push(customerNameHit(c))
  }

  // Tag universe: the catalog plus every tag on a current backend hit.
  const usage = new Map<string, number>()
  for (const h of backendHits.value) {
    for (const t of h.payload.tags) usage.set(t, (usage.get(t) ?? 0) + 1)
  }
  const universe = new Set<string>([...tagCatalog.value, ...usage.keys()])
  for (const tag of universe) {
    if (fuzzyMatchAny(nq, [tag])) out.push(tagHit(tag, usage.get(tag) ?? 0))
  }
  return out
})

// ── Ranked, grouped output ──────────────────────────────────────────
const merged = computed<SearchHit[]>(() => {
  const onView = new Set(context.value.onViewIds)
  return [...backendHits.value, ...clientHits.value].map((h) =>
    onView.has(h.id) ? { ...h, onView: true } : h,
  )
})

const groups = computed<Array<HitGroup<HitMeta>>>(() => rankAndGroup(merged.value, query.value))
const flatHits = computed<SearchHit[]>(() => groups.value.flatMap((g) => g.hits))
const hasQuery = computed(() => query.value.trim().length > 0)
const hasResults = computed(() => flatHits.value.length > 0)
const quickCards = computed<QuickCard[]>(() => context.value.quickCards)

// ── No-results escape hatch ─────────────────────────────────────────
// A query that matches nothing was a dead end (issue #2673). We now offer a
// way forward, rendered as ordinary keyboard-navigable hits so they reuse the
// modal's roving selection + activate path (no parallel key handling):
//   - "Mente du …?" suggestions — the nearest client-catalog entries by a
//     relaxed fuzzy tolerance. Selecting one opens that record directly via
//     its normal hit target.
//   - Escape-hatch actions — jump to the full customers / meetings list.
//
// Both are gated to the settled zero-hit state (query present, nothing loading,
// no error, no results) so `navHits` in the modal stays exactly the list the
// operator sees.

interface SuggestSource {
  label: string
  hit: () => SearchHit
}
// Catalogs are iterated generically — a new one joins suggestions by adding a
// single entry here, with no other change. Order is precedence on a distance
// tie (fuzzySuggestions keeps source order); customers lead because a typo'd
// customer name is the most common dead-end query.
const SUGGEST_CATALOGS: Array<() => SuggestSource[]> = [
  () => customersCache.value.map((c) => ({ label: c.name, hit: () => customerNameHit(c) })),
  () => servicesCache.value.map((s) => ({ label: s.name, hit: () => serviceHit(s) })),
  () => membersCache.value.map((m) => ({ label: m.name, hit: () => memberHit(m) })),
  () => tagCatalog.value.map((tag) => ({ label: tag, hit: () => tagHit(tag, 0) })),
]

const isSettledNoResults = computed(
  () => hasQuery.value && !hasResults.value && !error.value && !loading.value,
)

const noResultSuggestions = computed<SearchHit[]>(() => {
  if (!isSettledNoResults.value) return []
  const byLabel = new Map<string, SuggestSource>()
  for (const build of SUGGEST_CATALOGS) {
    for (const src of build()) {
      const key = src.label.trim().toLowerCase()
      if (key && !byLabel.has(key)) byLabel.set(key, src)
    }
  }
  const names = fuzzySuggestions(
    query.value,
    [...byLabel.values()].map((s) => s.label),
    3,
  )
  return names
    .map((n) => byLabel.get(n.trim().toLowerCase())?.hit())
    .filter((h): h is SearchHit => h != null)
})

function routeActionHit(id: string, labelKey: string, routeName: string): SearchHit {
  const label = t(labelKey)
  return {
    id: `action:${id}`,
    // Semantic destination type; these render inline (the modal never groups
    // them), so it only needs to be a valid SearchType.
    type: routeName === 'dashboard-meetings' ? 'mote' : 'kunde',
    title: label,
    ctx: '',
    payload: { target: { kind: 'route', id, label, to: { name: routeName } }, tags: [] },
  }
}

const noResultActions = computed<SearchHit[]>(() => {
  if (!isSettledNoResults.value) return []
  return [
    routeActionHit('all-customers', 'globalSearch.seeAllCustomers', 'dashboard-customers'),
    routeActionHit('all-meetings', 'globalSearch.seeAllMeetings', 'dashboard-meetings'),
  ]
})

// ── Fetch orchestration ─────────────────────────────────────────────
let searchToken = 0
let debounceTimer: ReturnType<typeof setTimeout> | null = null

function clearBackend() {
  backendHits.value = []
}

export function useGlobalSearch() {
  const router = useRouter()
  const { peek } = useFeatures()

  // ensureStaticData — lazily load services + team + customers + tag catalog
  // once, on first modal open. All best-effort: a failure just narrows the
  // result scope, it never blocks the (backend-driven) main search.
  async function ensureStaticData() {
    if (staticLoaded) return
    staticLoaded = true
    const [svc, team, customers, tags] = await Promise.all([
      api.get<{ services: Service[] }>('/api/admin/services').catch(() => ({ services: [] })),
      api.get<TeamResponse>('/api/admin/team').catch(() => ({ members: [] })),
      api
        .get<{ customers: Customer[] }>('/api/admin/customers')
        .catch(() => ({ customers: [] as Customer[] })),
      api
        .get<{ tags: string[] }>('/api/admin/documents/tags')
        .catch(() => ({ tags: [] as string[] })),
    ])
    servicesCache.value = svc.services ?? []
    membersCache.value = team.members ?? []
    customersCache.value = customers.customers ?? []
    tagCatalog.value = tags.tags ?? []
  }

  async function doFetch(q: string) {
    const myToken = ++searchToken
    loading.value = true
    error.value = null
    const apsPromise: Promise<ActionPointResponse> = peek('meetings.action-points')
      ? api
          .get<ActionPointResponse>(`/api/admin/action-items/search?q=${encodeURIComponent(q)}`)
          .catch(() => ({ action_points: [] }))
      : Promise.resolve({ action_points: [] })
    // Notes ride the authored-only decrypt-per-search endpoint. Best-effort:
    // a hiccup there degrades to no note hits rather than blanking the modal.
    const notesPromise: Promise<NoteResponse> = api
      .get<NoteResponse>(`/api/admin/notes/search?q=${encodeURIComponent(q)}`)
      .catch(() => ({ items: [] }))
    // Document content search — opt-in-indexable files only (health docs stay
    // out), gated on documents.search. Best-effort like the others.
    const docContentPromise: Promise<DocContentResponse> = peek('documents.search')
      ? api
          .get<DocContentResponse>(`/api/admin/documents/search?q=${encodeURIComponent(q)}`)
          .catch(() => ({ results: [] }))
      : Promise.resolve({ results: [] })
    // Message content search — decrypt-per-search over samtaler, gated on
    // messaging.search. Best-effort like the others.
    const messagesPromise: Promise<MessageResponse> = peek('messaging.search')
      ? api
          .get<MessageResponse>(`/api/admin/messages/search?q=${encodeURIComponent(q)}`)
          .catch(() => ({ items: [] }))
      : Promise.resolve({ items: [] })
    // Agenda-ask search — the meeting "Hva må vi få svar på?" points, gated on
    // meetings.agenda-search. Best-effort like the others.
    const agendaPromise: Promise<AskResponse> = peek('meetings.agenda-search')
      ? api
          .get<AskResponse>(`/api/admin/agenda/search?q=${encodeURIComponent(q)}`)
          .catch(() => ({ asks: [] }))
      : Promise.resolve({ asks: [] })
    // Tags rides the same best-effort posture as its five siblings (#2382):
    // the backend maps a down/noop Meili client to 503, and an uncaught 503
    // here used to reject the whole Promise.all, hit the outer catch, and
    // clearBackend() — blanking the modal even though notes/action-points/
    // messages/agenda/doc-content all returned hits. Degrade to "no tag
    // hits" instead, mirroring the sibling arms' empty shape.
    const tagsPromise: Promise<TagResponse> = api
      .get<TagResponse>(`/api/admin/search/tags?q=${encodeURIComponent(q)}`)
      .catch(() => ({ meetings: [], customers: [], documents: [] }))
    try {
      const [tags, aps, notes, docs, msgs, agenda] = await Promise.all([
        tagsPromise,
        apsPromise,
        notesPromise,
        docContentPromise,
        messagesPromise,
        agendaPromise,
      ])
      if (myToken !== searchToken) return
      // A document matching on content wins over the name/tag hit for the same
      // file — the content row carries the excerpt snippet.
      const contentDocs = docs.results ?? []
      const contentDocIds = new Set(contentDocs.map((d) => d.document_id))
      // pinned: the server already vouched for these, so they survive
      // client re-ranking even when their client-side fields don't
      // reproduce the match (e.g. a note matched on its encrypted body).
      backendHits.value = [
        ...(tags.meetings ?? []).map(meetingHit),
        ...(tags.customers ?? []).map(customerHit),
        ...(tags.documents ?? []).filter((d) => !contentDocIds.has(d.object_id)).map(documentHit),
        ...contentDocs.map(docContentHit),
        ...(aps.action_points ?? []).map(actionPointHit),
        ...(notes.items ?? []).map(noteHit),
        ...(msgs.items ?? []).map(messageHit),
        ...(agenda.asks ?? []).map(askHit),
      ].map((h) => ({ ...h, pinned: true }))
    } catch (e) {
      if (myToken !== searchToken) return
      error.value = errorMessage(e, 'globalSearch.errorGeneric')
      clearBackend()
    } finally {
      if (myToken === searchToken) loading.value = false
    }
  }

  // runSearch — debounced entry point. Empty query clears without a network
  // hit (the empty-state renders recent + quick-jumps instead).
  function runSearch(raw: string) {
    query.value = raw
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    const q = raw.trim()
    if (!q) {
      searchToken++
      clearBackend()
      error.value = null
      loading.value = false
      return
    }
    // Client hits (tjenester/team/tagger) recompute reactively from `query`
    // with no network, so they render immediately — loading is only a subtle
    // hint while the debounced backend round-trip catches up (#2387). Clear any
    // stale error from a previous failed fetch so it can't mask the fresh
    // local results during the debounce window.
    error.value = null
    loading.value = true
    debounceTimer = setTimeout(() => void doFetch(q), DEBOUNCE_MS)
  }

  // pushRecent — remember a term the operator acted on (opened a hit for, or
  // tapped a quick-jump). De-duped, most-recent first, capped.
  function pushRecent(term: string) {
    const t = term.trim()
    if (!t) return
    recent.value = [t, ...recent.value.filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(
      0,
      RECENT_MAX,
    )
    persistRecent()
  }
  function clearRecent() {
    recent.value = []
    persistRecent()
  }

  function setContext(ctx: SearchContext) {
    context.value = ctx
  }

  // navigateToHit — route to the object's surface. Tag hits don't navigate
  // (the modal intercepts them to re-run the search); every other kind lands
  // on its detail/hub page.
  function navigateToHit(target: GlobalSearchTarget) {
    switch (target.kind) {
      case 'meeting':
        void router.push(`/dashboard/meetings/${target.id}`)
        break
      case 'customer':
        void router.push({ name: 'dashboard-customer-detail', params: { id: target.id } })
        break
      case 'document':
        void router.push('/dashboard/documents')
        break
      case 'action_point':
        if (target.booking_id) void router.push(`/dashboard/meetings/${target.booking_id}`)
        else if (target.customer_id)
          void router.push({
            name: 'dashboard-customer-detail',
            params: { id: target.customer_id },
          })
        else void router.push('/dashboard')
        break
      case 'note':
        if (target.booking_id) void router.push(`/dashboard/meetings/${target.booking_id}`)
        else void router.push({ name: 'dashboard-note-detail', params: { entryId: target.id } })
        break
      case 'message':
        // Land in the conversation when the thread-view surface is on and we
        // know the customer; otherwise fall back to the customer detail page
        // (its inline timeline shows the samtale). Threads without a linked
        // customer row have nowhere richer to go than the dashboard.
        if (target.customer_id && peek('messaging.thread-view')) {
          void router.push({
            name: 'dashboard-customer-conversation',
            params: { id: target.customer_id },
          })
        } else if (target.customer_id) {
          void router.push({
            name: 'dashboard-customer-detail',
            params: { id: target.customer_id },
          })
        } else {
          void router.push('/dashboard')
        }
        break
      case 'service':
        void router.push({ name: 'dashboard-services' })
        break
      case 'member':
        void router.push({ name: 'dashboard-settings' })
        break
      case 'tag':
        // Handled in-modal (re-query); nothing to route to.
        break
      case 'route':
        // No-results escape-hatch action: jump to the list page it names.
        if (target.to) void router.push(target.to)
        break
    }
  }

  function reset() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    searchToken++
    query.value = ''
    clearBackend()
    error.value = null
    loading.value = false
  }

  return {
    query,
    loading,
    error,
    groups,
    flatHits,
    hasQuery,
    hasResults,
    noResultSuggestions,
    noResultActions,
    recent,
    quickCards,
    runSearch,
    pushRecent,
    clearRecent,
    setContext,
    ensureStaticData,
    navigateToHit,
    reset,
  }
}
